import "./Chat.css";

import {
  CreateAnimation,
  IonActionSheet,
  IonBackButton,
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonPage,
  IonRow,
  IonTextarea,
  IonToast,
  IonToolbar,
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addOutline,
  alertOutline,
  callOutline,
  cameraOutline,
  checkmarkDone,
  arrowBackCircleOutline,
  micOutline,
  send,
  shareOutline,
  starOutline,
  trashOutline,
  videocamOutline,
  attachOutline,
  chevronBack,
  lockOpen,
  lockClosed,
  lockClosedOutline,
  documentLockOutline,
  documentOutline,
} from "ionicons/icons";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { FRONTEND_URL } from "../../api/client";
import {
  getMessages,
  sendMessage,
  sendMessageWithMedia,
  getAllChats,
  deleteChat,
} from "../../api/requests/chat";
import { blockUser } from "../../api/requests/profile";
import { useSocket } from "../../api/useSocket";
import Spinner from "../../components/Spinner";
import { markAsRead } from "../../redux/actions/chatActions";
import { chatSlicer } from "../../redux/slicers/chatSlicer";
import { formatMessageTime, formatDayHeader } from "../../utils/getDate";
import { format } from "date-fns";
import { useCamera } from "../../hooks/useCamera";
import { useGallery } from "../../hooks/useGallery";
import { useLongPress } from "use-long-press";
import { pathToFile } from "../../utils/getPathToFile";
import { DEFAULT_HOME_PATH } from "../../utils/constents";
import Avatar from "../../components/avatar/Avatar";
import { Helmet } from "react-helmet-async";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera"; // <--- Add this import

export const getChatUser = (activeChat, currentProfile) => {
  // Be defensive: users may be ObjectIds or populated user docs
  if (
    !activeChat ||
    !Array.isArray(activeChat.users) ||
    activeChat.users.length < 2 ||
    !currentProfile
  ) {
    return null;
  }
  const [u0, u1] = activeChat.users;
  const u0Id = typeof u0 === "string" || typeof u0 === "number" ? u0 : u0?._id;
  const u1Id = typeof u1 === "string" || typeof u1 === "number" ? u1 : u1?._id;

  if (!u0Id || !u1Id) return null;

  // If users are populated, return the opposite populated user object; otherwise we can't build chatUser yet
  if (u0 && u0._id && String(u0._id) !== String(currentProfile._id)) return u0;
  if (u1 && u1._id && String(u1._id) !== String(currentProfile._id)) return u1;
  return null;
};

const Chat = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const socket = useSocket();
  const allUnread = useSelector((state) => state.chat.unread);
  const { id } = useParams();
  const [activeChat, setActiveChat] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const currentProfile = useSelector((state) => state.currentProfile.data);
  const chatUser = getChatUser(activeChat, currentProfile);
  const chatState = useSelector((state) => state.chat);
  const { chats, unread, messages } = chatState;

  const dispatch = useDispatch();
  const location = useLocation();

  const { takePhoto } = useCamera();
  const { prompt } = useGallery();

  //  Local state
  const [message, setMessage] = useState("");
  const [showSendButton, setShowSendButton] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [showActionSheet, setShowActionSheet] = useState(false);
  const [actionMessage, setActionMessage] = useState(false);
  const [showAttachActionSheet, setShowAttachActionSheet] = useState(false);
  //  Refs
  const contentRef = useRef();
  const swiperRefs = useRef([]);
  const textareaRef = useRef();
  const sideRef = useRef();
  const sendRef = useRef();

  const comingSoon = () => {
    toaster("Coming soon!");
  };
  // Inside the Chat component, after other handlers:
  const handleAttachClick = () => {
    setShowAttachActionSheet(true);
  };

  const goToLoonlock = () => {
    // Assuming you want to navigate to this link.
    // Since you are using react-router-dom, you'll need the history object.
    // Add: import { useHistory } from "react-router-dom";
    // Add: const history = useHistory();
    // ... then use history.push('/loonlock');
    // toaster("Navigating to /loonlock (Not implemented)");
    // You would replace the toaster with history.push('/loonlock');
    router.push("/loonlock");
  };
  // Inside the Chat component, define the buttons:
  const attachSheetButtons = [
    {
      text: "From Photos", // Customize this text
      icon: cameraOutline, // Reusing camera icon for photos
      handler: () => {
        // Must call handlePrompt after closing the action sheet
        setShowAttachActionSheet(false);
        handlePrompt();
      },
    },
    {
      text: "Take Picture", // Customize this text
      icon: cameraOutline,
      handler: () => {
        // Must call handlePhoto after closing the action sheet
        setShowAttachActionSheet(false);
        handlePhoto();
      },
    },
    {
      text: "🔐 Use LoonLock (Encrypted)", // Your new button text
      // icon: , // Choose a suitable icon
      handler: () => {
        setShowAttachActionSheet(false);
        goToLoonlock();
      },
    },
    {
      text: "Cancel",
      role: "cancel",
    },
  ];
  const actionSheetButtons = [
    {
      text: false ? "Unstar Message" : "Star Message",
      icon: starOutline,
      handler: comingSoon,
    },
    actionMessage && actionMessage.sender === currentProfile._id
      ? {
          text: "Reply To Message",
          icon: shareOutline,
          handler: comingSoon,
        }
      : {
          text: "Unsend Message",
          icon: alertOutline,
          handler: comingSoon,
        },
    {
      text: "Delete Message",
      icon: trashOutline,
      handler: comingSoon,
      role: "destructive",
    },
  ];

  // Effects
  useIonViewWillEnter(() => {
    if (location) {
      const query = new URLSearchParams(location.search);
      const postId = query.get("post");
      if (postId) setMessage(FRONTEND_URL + "/post/" + postId);
    }
    dispatch(chatSlicer.actions.setActiveChat(id));

    if (unread?.chats?.find(({ chatId }) => chatId === id)?.count > 0)
      dispatch(markAsRead(id));

    scrollToBottom();
    setupObserver();
  }, [location, id, dispatch, unread]);

  useIonViewWillLeave(() => {
    dispatch(chatSlicer.actions.setActiveChat(""));
  });

  useEffect(() => {
    const initActiveFromStore = () => {
      if (chats && chats.length > 0) {
        const chat = chats.find((c) => c._id === id);
        if (chat) {
          setActiveChat(chat);
        }
      }
    };
    initActiveFromStore();
  }, [chats, id]);

  useEffect(() => {
    const fetchMessagesFunc = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const { data } = await getMessages(id);
        dispatch(chatSlicer.actions.setMessages(data));
        setActiveChat((prev) => {
          if (prev) return prev; // Retain properly populated object from initActiveFromStore

          const first = Array.isArray(data) && data.length > 0 ? data[0] : null;
          if (first && first.chatId) {
            dispatch(chatSlicer.actions.addChat(first.chatId));
            return first.chatId;
          }

          // No messages - attempt secondary fetch if needed
          getAllChats()
            .then(({ data: allChats }) => {
              const chat =
                Array.isArray(allChats) && allChats.find((c) => c._id === id);
              if (chat) {
                setActiveChat(chat);
                dispatch(chatSlicer.actions.addChat(chat));
              } else {
                setActiveChat({ _id: id, users: [] });
              }
            })
            .catch((e) => {
              console.error("Failed to fetch chat details", e);
              setActiveChat({ _id: id, users: [] });
            });

          return prev;
        });
        if (socket) socket.emit("join room", id);
      } catch (e) {
        console.error("Failed to fetch messages for chat", id, e);
      } finally {
        setLoading(false);
      }
    };

    fetchMessagesFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  //  Scroll to end of content
  const scrollToBottom = async () => {
    if (contentRef.current) {
      contentRef.current.scrollToBottom();
    }
  };

  //  Watch for DOM changes
  //  Then scroll to bottom
  //  This ensures that the new chat message has *actually* been rendered
  //  Check this:
  //  https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
  const setupObserver = () => {
    if (!contentRef.current) return;
    //  Mutation Observers watch for DOM changes
    //  This will ensure that we scroll to bottom AFTER the new chat has rendered
    const observer = new MutationObserver(() => {
      scrollToBottom();
    });

    //  We observe the ion-content (or containing element of chats)
    observer.observe(contentRef.current, {
      childList: true,
    });
  };
  //  Long press callback
  const onLongPress = (e) => {
    const elementID = e.target.id;
    const chatMessageID = elementID.includes("chatText")
      ? parseInt(elementID.replace("chatText_", ""))
      : elementID.includes("chatTime")
        ? parseInt(elementID.replace("chatTime_", ""))
        : parseInt(elementID.replace("chatBubble_", ""));

    const chatMessage = messages.find(
      (message) => message._id === chatMessageID,
    );

    setActionMessage(chatMessage);
    setShowActionSheet(true);
  };

  const longPress = useLongPress(onLongPress, {
    // isPreventDefault: true,
    // delay: 2000,
  });

  //  For displaying toast messages
  const toaster = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const widthAnimation = {
    property: "width",
    fromValue: "110%",
    toValue: "100%",
  };

  const fadeAnimation = {
    property: "opacity",
    fromValue: "100%",
    toValue: "0%",
  };

  const sideButtonsAnimation = {
    duration: 200,
    direction: showSendButton ? "normal" : "reverse",
    iterations: "1",
    fromTo: [fadeAnimation],
    easing: "ease-in-out",
  };

  const sendButtonAnimation = {
    duration: showSendButton ? 300 : 100,
    direction: !showSendButton ? "normal" : "reverse",
    iterations: "1",
    fromTo: [fadeAnimation],
    easing: "ease-in-out",
  };

  const textareaAnimation = {
    duration: 200,
    direction: !showSendButton ? "normal" : "reverse",
    iterations: "1",
    fromTo: [widthAnimation],
    easing: "ease-in-out",
  };

  //  Set the state value when message val changes
  useEffect(() => {
    setShowSendButton(message !== "");
  }, [message]);

  //  Play the animations when the state value changes
  useEffect(() => {
    if (textareaRef.current && sideRef.current && sendRef.current) {
      textareaRef.current.animation.play();
      sideRef.current.animation.play();
      sendRef.current.animation.play();
    }
  }, [showSendButton]);

  const keyDownFunction = async (e) => {
    if (!activeChat) return;
    if (((e.key === "Enter" && !e.shiftKey) || e.type === "click") && message) {
      e.preventDefault();
      setIsSending(true);
      setMessage("");

      const response = await sendMessage(activeChat._id, { message });
      if (response && response.status !== 200) {
        toaster(response.data?.error || "Failed to send message.");
        setIsSending(false);
        return;
      }

      const { data } = response;
      dispatch(chatSlicer.actions.addMessage(data));
      dispatch(markAsRead(id));
      setIsSending(false);
      if (socket) socket.emit("new message", data);
    }
  };
  const handlePhoto = async () => {
    if (!activeChat) return;
    try {
      // const returnedFilePath = await takePhoto();
      // const messageData = {
      //   sender: currentProfile._id,
      //   message: "",
      //   image: true,
      //   imagePath: returnedFilePath,
      //   createdAt: new Date().toISOString(),
      //   chatId: {
      //     ...activeChat,
      //     users: activeChat.users.map((user) => user._id),
      //   },
      // };
      // dispatch(chatSlicer.actions.addMessage(messageData));
      // if (socket) socket.emit("new message", messageData);
      setIsSending(true);
      const webPath = await takePhoto();
      const file = await pathToFile(
        typeof webPath === "string" ? webPath : webPath.webPath,
        "camera.jpg",
      );
      const response = await sendMessageWithMedia(activeChat._id, {
        message: "", // or use current `message` as a caption if you want
        file,
      });

      if (response && response.status !== 200) {
        throw new Error(response.data?.error || "Failed to send photo.");
      }

      const { data } = response;
      dispatch(chatSlicer.actions.addMessage(data));
      dispatch(markAsRead(id));
      if (socket) socket.emit("new message", data);
    } catch (error) {
      console.error("Error taking photo:", error);
      toaster("Failed to send photo.");
    } finally {
      setIsSending(false);
    }
  };
  const handlePrompt = async () => {
    if (!activeChat) return;

    try {
      setIsSending(true);

      // FIX: Use Camera.getPhoto directly with source: CameraSource.Photos
      // This skips the "From Photos / Take Picture" native menu (Screenshot 1)
      const picked = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos, // <--- This forces the Gallery to open immediately
      });

      const file = await pathToFile(picked.webPath, "gallery.jpg");

      const response = await sendMessageWithMedia(activeChat._id, {
        message: "",
        file,
      });

      if (response && response.status !== 200) {
        throw new Error(response.data?.error || "Failed to send photo.");
      }

      const { data } = response;
      dispatch(chatSlicer.actions.addMessage(data));
      dispatch(markAsRead(id));
      if (socket) socket.emit("new message", data);
    } catch (error) {
      console.error("Error selecting photo:", error);
      toaster(error.message || "Failed to send photo.");
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteChat = async () => {
    if (!activeChat) return;
    try {
      setLoading(true);
      await deleteChat(activeChat._id);
      toaster("Chat deleted successfully");
      router.push("/messages");
    } catch (error) {
      console.error(error);
      setLoading(false);
      toaster("Failed to delete chat");
    }
  };

  const handleBlockUser = async () => {
    if (!chatUser || !chatUser.username) return;
    try {
      setLoading(true);
      await blockUser(chatUser.username);
      toaster("User blocked successfully");
      await deleteChat(activeChat._id);
      router.push("/messages");
    } catch (error) {
      console.error(error);
      setLoading(false);
      toaster("Failed to block user");
    }
  };

  // const handlePrompt = async () => {
  //   if (!activeChat) return;
  //   // try {
  //   //   const returnedFilePath = await prompt();
  //   //   console.log("activechat", activeChat);
  //   //   const messageData = {
  //   //     sender: currentProfile._id,
  //   //     message: "",
  //   //     image: true,
  //   //     imagePath: returnedFilePath,
  //   //     createdAt: new Date().toISOString(),
  //   //     chatId: {
  //   //       ...activeChat,
  //   //       users: activeChat.users.map((user) => user._id),
  //   //     },
  //   //   };
  //   //   dispatch(chatSlicer.actions.addMessage(messageData));
  //   //   if (socket) socket.emit("new message", messageData);
  //   // } catch (error) {
  //   //   console.error("Error selecting photo:", error);
  //   // }
  //   try {
  //     setIsSending(true);
  //     const picked = await prompt(); // could be a File or a { webPath }
  //     const file =
  //       picked instanceof File
  //         ? picked
  //         : await pathToFile(
  //             typeof picked === "string" ? picked : picked.webPath,
  //             "gallery.jpg"
  //           );
  //     const { data } = await sendMessageWithMedia(activeChat._id, {
  //       message: "", // or use current `message` as a caption
  //       file,
  //     });
  //     dispatch(chatSlicer.actions.addMessage(data));
  //     dispatch(markAsRead(id));
  //     if (socket) socket.emit("new message", data);
  //   } catch (error) {
  //     console.error("Error selecting photo:", error);
  //     toaster("Failed to send photo.");
  //   } finally {
  //     setIsSending(false);
  //   }
  // };

  // const sendMessage = (image = false, imagePath = false) => {
  //   if (message !== "" || image === true) {
  //     sendChatMessage(
  //       params.contact_id,
  //       message,
  //       replyToMessage,
  //       replyToMessage ? replyToMessage.id : false,
  //       image,
  //       imagePath
  //     );
  //     setMessage("");

  //     image && setTimeout(() => scrollToBottom(), 100);
  //   }
  // };
  const truncate = (str, maxLength) => {
    if (!str) return "";
    return str.length > maxLength ? str.slice(0, maxLength - 1) + "…" : str;
  };

  // Generic grouping function
  const groupBy = (items, getKey) => {
    if (!items?.length) return [];
    const groups = [];
    let currentKey = null;
    let currentGroup = [];

    items.forEach((item) => {
      const key = getKey(item);
      if (currentKey !== key) {
        if (currentGroup.length) {
          groups.push({ key: currentKey, items: currentGroup });
        }
        currentKey = key;
        currentGroup = [item];
      } else {
        currentGroup.push(item);
      }
    });

    if (currentGroup.length) {
      groups.push({ key: currentKey, items: currentGroup });
    }

    return groups;
  };

  // Group messages by day and then by sender
  const organizeMessages = (messages) => {
    const reversed = [...messages].reverse();
    const dayGroups = groupBy(reversed, (msg) =>
      format(new Date(msg.createdAt), "yyyy-MM-dd"),
    );

    return dayGroups.map((dayGroup) => ({
      day: dayGroup.key,
      date: new Date(dayGroup.items[0].createdAt),
      senderGroups: groupBy(dayGroup.items, (msg) => msg.sender).map(
        (sg) => sg.items,
      ),
    }));
  };
  const linkifyHashtag = (text) => {
    if (!text) return "";

    // Regex looks for the specific URL pattern followed by non-whitespace characters
    const regex = /(https:\/\/loonlog\.ca\/hashtag\/[^\s]+)/g;

    const parts = text.split(regex);

    return parts.map((part, i) => {
      if (part.match(regex)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#fff", textDecoration: "underline" }}
            onClick={(e) => e.stopPropagation()} // Prevents long-press/bubble clicks
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };
  // MessageBubble component
  const MessageBubble = ({
    message,
    isSent,
    isFirst,
    isLast,
    index,
    onLongPress,
  }) => {
    return (
      <div
        className={`chat-message-container ${
          isSent ? "message-sent" : "message-received"
        } ${isFirst ? "message-group-first" : "message-group-continued"}`}
      >
        <div
          ref={(ref) => (swiperRefs.current[index] = ref)}
          id={`chatBubble_${message._id}`}
          className={`chat-bubble ${
            isSent ? "bubble-sent" : "bubble-received"
          } ${!isFirst ? "bubble-continued" : ""}`}
          {...onLongPress()}
        >
          <div id={`chatText_${message._id}`}>
            <div className="text-body">{linkifyHashtag(message.message)}</div>
            {message.media?.url && (
              <img src={message.media.url} alt="chat message" />
            )}
            <span
              className="chat-bottom-details"
              id={`chatTime_${message._id}`}
            >
              {isSent && (
                <IonIcon
                  icon={checkmarkDone}
                  color="primary"
                  style={{ fontSize: "0.8rem" }}
                />
              )}
            </span>
          </div>
        </div>
        {isLast && (
          <span className="chat-message-time">
            {formatMessageTime(message.createdAt)}
          </span>
        )}
      </div>
    );
  };

  // Render messages
  const renderMessages = () => {
    if (!messages) return null;

    const organized = organizeMessages(messages);
    let messageIndex = 0;

    return organized.map((dayGroup) => (
      <div key={dayGroup.day} className="chat-day-group">
        <div className="chat-day-header">{formatDayHeader(dayGroup.date)}</div>
        {dayGroup.senderGroups.map((senderGroup) =>
          senderGroup.map((message, msgIndex) => {
            const isSent = message.sender === currentProfile._id;
            const isFirst = msgIndex === 0;
            const isLast = msgIndex === senderGroup.length - 1;
            const index = messageIndex++;

            return (
              <MessageBubble
                key={message._id || `${dayGroup.day}-${index}`}
                message={message}
                isSent={isSent}
                isFirst={isFirst}
                isLast={isLast}
                index={index}
                onLongPress={longPress}
              />
            );
          }),
        )}
      </div>
    ));
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <IonPage className="chat-page">
        <IonHeader mode="ios">
          <IonToolbar>
            <div className="chat-contact-container">
              <div className="chat-contact-start">
                <IonBackButton
                  slot="start"
                  text={allUnread > 0 ? allUnread : ""}
                  defaultHref={DEFAULT_HOME_PATH}
                  icon={chevronBack}
                  style={{ color: "#e04d61", fontSize: "1.4rem" }}
                />
              </div>
              <div className="chat-contact">
                <Avatar
                  username={chatUser?.username}
                  avatar={chatUser?.avatar}
                  province={chatUser?.province}
                  size="md"
                  isOfficial={chatUser?.isAdmin}
                />
                {/* <img src={chatUser?.avatar} alt="avatar" /> */}
                <div className="chat-contact-details">
                  <p
                    className="chat-username"
                    title={`@${chatUser?.username}`}
                    aria-label={`@${chatUser?.username}`}
                  >
                    @{truncate(chatUser?.username, 20)}
                  </p>
                </div>
              </div>
              <div className="chat-contact-end">
                <IonButton fill="clear" size="small" onClick={comingSoon}>
                  <IonIcon icon={videocamOutline} />
                </IonButton>
                <IonButton fill="clear" size="small" onClick={comingSoon}>
                  <IonIcon icon={callOutline} />
                </IonButton>
              </div>
            </div>
          </IonToolbar>
        </IonHeader>

        <IonContent id="main-chat-content" ref={contentRef}>
          {loading || !activeChat ? (
            <Spinner />
          ) : messages ? (
            renderMessages()
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              {t("notFound")}
            </div>
          )}
          <IonActionSheet
            header="Message Actions"
            subHeader={actionMessage && actionMessage.message}
            isOpen={showActionSheet}
            onDidDismiss={() => setShowActionSheet(false)}
            buttons={actionSheetButtons}
          />
          <IonActionSheet
            header="Attach Media or Link"
            isOpen={showAttachActionSheet}
            onDidDismiss={() => setShowAttachActionSheet(false)}
            buttons={attachSheetButtons}
          />
          <IonToast
            color="primary"
            isOpen={showToast}
            onDidDismiss={() => setShowToast(false)}
            message={toastMessage}
            position="bottom"
            duration="3000"
          />
        </IonContent>
        <IonFooter className="chat-footer" id="chat-footer">
          {activeChat?.isRequest &&
            messages?.length > 0 &&
            messages.some((m) => m.sender !== currentProfile._id) && (
              <div
                style={{
                  backgroundColor: "var(--ion-color-light, #f4f5f8)",
                  padding: "10px",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  color: "var(--ion-color-medium, #92949c)",
                  borderTop: "1px solid var(--border)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <div>
                  {t("By replying to this message you accept the request")}
                </div>
                <div style={{ display: "flex", gap: "15px" }}>
                  <IonButton
                    fill="outline"
                    color="danger"
                    size="small"
                    onClick={handleDeleteChat}
                    style={{ "--border-radius": "20px" }}
                  >
                    {t("Delete")}
                  </IonButton>
                  <IonButton
                    fill="outline"
                    color="danger"
                    size="small"
                    onClick={handleBlockUser}
                    style={{ "--border-radius": "20px" }}
                  >
                    {t("Block")}
                  </IonButton>
                </div>
              </div>
            )}
          <IonGrid>
            <IonRow className="ion-align-items-center">
              <IonCol size="1">
                <IonIcon
                  icon={attachOutline}
                  color="primary"
                  // onClick={handlePrompt}
                  onClick={handleAttachClick}
                  // className="msgIcon"
                  style={{
                    fontSize: "1.8rem",
                    paddingTop: "12px",
                    paddingLeft: "4px",
                  }}
                />
              </IonCol>

              <div className="chat-input-container">
                <CreateAnimation ref={textareaRef} {...textareaAnimation}>
                  <IonTextarea
                    rows="1"
                    value={message}
                    onIonInput={(e) => setMessage(e.target.value)}
                    onKeyDown={keyDownFunction}
                    style={{
                      background: "white",
                      border: "none",
                      paddingTop: "5px",
                      fontSize: "1rem",
                    }}
                    placeholder={t("typeMessagePlaceholder")}
                  />
                </CreateAnimation>
              </div>

              <CreateAnimation ref={sideRef} {...sideButtonsAnimation}>
                <IonCol size="1">
                  <IonIcon
                    icon={cameraOutline}
                    color="primary"
                    onClick={handlePhoto}
                  />
                </IonCol>

                <IonCol size="1">
                  <IonIcon
                    icon={micOutline}
                    color="primary"
                    onClick={() => toaster("Coming soon!")}
                  />
                </IonCol>
              </CreateAnimation>

              <CreateAnimation ref={sendRef} {...sendButtonAnimation}>
                <IonCol
                  size="1"
                  className="chat-send-button"
                  onClick={(e) => keyDownFunction(e)}
                  aria-disabled={isSending}
                >
                  <IonIcon icon={send} />
                </IonCol>
              </CreateAnimation>
            </IonRow>
          </IonGrid>
        </IonFooter>
      </IonPage>
    </>
  );
};

export default Chat;

